import { Injectable, Injector, Inject } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { FcpToastComponent } from './fcp-toast.component';
import { ToastData, TOAST_CONFIG_TOKEN, ToastConfig } from './toast-config';
import { ToastRef } from './toast-ref';

@Injectable({
    providedIn: 'root'
})
export class FcpToastService {
    private toasts: ToastRef[] = [];
    private lastBottomPosition = this.toastConfig.position.bottom;

    constructor(
        private overlay: Overlay,
        private parentInjector: Injector,
        @Inject(TOAST_CONFIG_TOKEN) private toastConfig: ToastConfig
    ) {}

    show(data: ToastData) {
        const positionStrategy = this.getPositionStrategy(this.toasts.length);
        const overlayRef = this.overlay.create({ positionStrategy });

        const toastRef = new ToastRef(overlayRef);
        toastRef.closed.subscribe(() => {
            this.toasts = this.toasts.filter((t) => t !== toastRef);
            this.repositionToasts();
        });
        this.toasts.push(toastRef);

        const injector = this.getInjector(
            {
                ...data,
                timeout: data.timeout ?? 3000,
                showClose: data.showClose ?? false,
                showIcon: data.showIcon ?? true,
                icon: data.icon ?? ['fal', 'check-circle']
            },
            toastRef,
            this.parentInjector
        );
        const toastPortal = new ComponentPortal(FcpToastComponent, null, injector);

        overlayRef.attach(toastPortal);

        return toastRef;
    }

    repositionToasts() {
        this.toasts.forEach((toast, i) => {
            toast.setPositionStrategy(this.getPositionStrategy(i));
        });
    }

    getPositionStrategy(index = 0) {
        return this.overlay.position().global().bottom(this.getPosition(index)).centerHorizontally();
    }

    getPosition(index: number) {
        const beforeToast = this.toasts[index - 1];
        const beforeToastIsVisible = beforeToast && beforeToast.isVisible();
        if (beforeToastIsVisible) {
            const beforeToastPosition = beforeToast.getPosition();
            this.lastBottomPosition += beforeToastPosition.bottom - beforeToastPosition.top;
            return this.lastBottomPosition + 'px';
        }

        this.lastBottomPosition = this.toastConfig.position.bottom;
        return `${this.toastConfig.position.bottom}px`;
    }

    getInjector(data: ToastData, toastRef: ToastRef, parentInjector: Injector) {
        return Injector.create({
            parent: parentInjector,
            providers: [
                { provide: ToastData, useValue: data },
                { provide: ToastRef, useValue: toastRef }
            ]
        });
    }
}
